import { afterNextRender, Component, effect, inject, Injector, input, untracked } from '@angular/core';
import { registerables } from 'chart.js';
import ChartJS from 'chart.js/auto';
import { AppState } from '@services/core/app-state';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import { commas } from '@helpers';
import type {
  BarChartDataset,
  GroupedBarsChartDataset,
  DoughnutChartDataset,
  LineChartDataset,
  HorizontalBarChartDataset,
  StackedBarLineChartDataset,
  TreemapChartDataset,
} from '@appTypes';
import { CSSValuePipe } from '@pipes/css-value-pipe';
ChartJS.register(...registerables, ChartDataLabels, TreemapController, TreemapElement);

type ChartConfigType = {
  config: {
    options: {
      scales: {
        x: {
          ticks: {
            color: string,
          },
          grid: {
            color: string,
          }
        },
        y: {
          ticks: {
            color: string,
          },
          grid: {
            color: string,
          },
        },
      },
    },
  },
};

@Component({
  selector: 'app-chart',
  standalone: true,
  imports: [ CSSValuePipe ],
  templateUrl: './chart.html',
  host: {
    '[style.width]': '$width()',
    '[style.height]': '$height()',
  },
})
export class Chart {
  // Dependencias
  private readonly injector = inject(Injector);
  private readonly appState = inject(AppState);

  // Style inputs
  $width = input<string>('100%', { alias: 'width' });
  $height = input<string>('100%', { alias: 'height' });
  $margin = input<string>('0', { alias: 'margin' });
  $padding = input<string>('0', { alias: 'padding' });

  // Chart inputs
  $displayTopLabels = input<boolean>(false, { alias: 'displayTopLabels' });
  $dataset = input.required<BarChartDataset | GroupedBarsChartDataset | DoughnutChartDataset | LineChartDataset | HorizontalBarChartDataset | StackedBarLineChartDataset | TreemapChartDataset>({ alias: 'dataset' });
  $responsive = input<boolean | undefined>(undefined, { alias: 'responsive' }); // Si es "true" la gráfica no será responsiva
  $mantainAspectRatio = input<boolean | undefined>(undefined, { alias: 'maintainAspectRatio' });
  $canvasRadius = input<string>('0', { alias: 'canvasRadius' });
  $canvasWidth = input<string | undefined>(undefined, { alias: 'canvasWidth' });
  $justify = input<'justify-center' | 'justify-start' | 'justify-end'>('justify-center', { alias: 'justify' });
  // Barras horizontales: escala logarítmica por defecto (mismo criterio que so-chart-ui) para
  // que categorías con valores muy dispares (ej. 32,455 vs 9) sigan siendo visibles como barra.
  $useLogarithmicScale = input<boolean>(true, { alias: 'useLogarithmicScale' });

  // Signals / atributos
  protected CHART_ID = 'chart_' + Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
  private canvas!: HTMLCanvasElement;
  private chart: ChartJS | null = null;
  private readonly LIGHT_TICKS_COLOR = '#002a7';
  private readonly DARK_TICKS_COLOR = '#e7e7e7';
  private readonly LIGHT_GRID_COLOR = '#e7e7e7';
  private readonly DARK_GRID_COLOR = '#44444f';
  private ticksColor: string = this.LIGHT_TICKS_COLOR;
  private gridColor: string = this.LIGHT_GRID_COLOR;

  // Constructor
  constructor() {
    ChartJS.defaults.font.size = 15;
    ChartJS.defaults.font.family = 'Montserrat';

    afterNextRender(() => {
      this.canvas = document.getElementById(this.CHART_ID) as HTMLCanvasElement;
      this.effectHandler();
    });

    effect(() => {
      this.applyTheme(this.appState.$theme());
    });
  }

  // Aplica los colores del tema actual a la gráfica
  private applyTheme(theme: string) {
    if (!this.chart) return;
    const isDark = theme === 'dark';
    this.ticksColor = isDark ? this.DARK_TICKS_COLOR : this.LIGHT_TICKS_COLOR;
    this.gridColor = isDark ? this.DARK_GRID_COLOR : this.LIGHT_GRID_COLOR;
    try {
      (this.chart as unknown as ChartConfigType).config.options.scales.x.grid.color = this.gridColor;
      (this.chart as unknown as ChartConfigType).config.options.scales.y.grid.color = this.gridColor;
      (this.chart as unknown as ChartConfigType).config.options.scales.x.ticks.color = this.ticksColor;
      (this.chart as unknown as ChartConfigType).config.options.scales.y.ticks.color = this.ticksColor;
    }
    catch {}
    this.chart.update();
  }

  // Crea la gráfica necesaria en función del dataset establecido
  private effectHandler() {
    effect(() => {
      const dataset = this.$dataset();

      requestAnimationFrame(() => {
        switch (dataset.type) {
          case 'bar':
            this.createBarChart(dataset as BarChartDataset);
            break;
          case 'groupedBars':
            this.createGroupedBarsChart(dataset as GroupedBarsChartDataset);
            break;
          case 'horizontalBar':
            this.createHorizontalBarChart(dataset as HorizontalBarChartDataset);
            break;
          case 'stackedBarLine':
            this.createStackedBarLineChart(dataset as StackedBarLineChartDataset);
            break;
          case 'line':
            this.createLineChart(dataset as LineChartDataset);
            break;
          case 'doughnut':
            this.createDoughnutChart(dataset as DoughnutChartDataset);
            break;
          case 'treemap':
            this.createTreemapChart(dataset as TreemapChartDataset);
            break;
        }

        this.applyTheme(untracked(() => this.appState.$theme()));
      });

    }, { injector: this.injector });
  }

  // Devuelve el tamaño adecuado de las barras para una gráfica en función de la cantidad de estas
  private getBarChartBarsPercentage(datasetsAmount: number): number {
    if (datasetsAmount == 1)
      return .8;
    else if (datasetsAmount == 2)
      return .9;
    else if (datasetsAmount < 7)
      return .8;
    else if (datasetsAmount <= 10)
      return .7;
    else if (datasetsAmount <= 12)
      return .75;
    else
      return 1;
  };
  private createBarChart(dataset: BarChartDataset) {
    if (!dataset.data.length) return;

    if (dataset.labels.length > 0 && dataset.labels.length !== dataset.data.length)
      throw Error(`Gráfica de barras - la cantidad de etiquetas (${ dataset.labels.length }) es distinta a la cantidad de datos (${ dataset.data.length }).`);
    if (dataset.colors.length !== dataset.data.length)
      throw Error(`Gráfica de barras - la cantidad de colores (${ dataset.colors.length }) es distinta a la cantidad de datos (${ dataset.data.length }).`);

    const barPercentage = this.getBarChartBarsPercentage(dataset.data.length);

    if (this.chart)
      this.chart.destroy();
    this.chart = new ChartJS(this.canvas, {
      type: 'bar',
      data: {
        labels: [''],
        datasets: dataset.data.map((data, index) => {
          return {
            data: [ data ],
            label: dataset.labels.length > 0 ? dataset.labels[index] : undefined,
            backgroundColor: dataset.colors[index],
            borderWidth: 0,
            barPercentage,
          };
        }),
      },
      options: {
        responsive: typeof this.$responsive() === 'boolean' ? this.$responsive() as boolean : true,
        maintainAspectRatio: typeof this.$mantainAspectRatio() === 'boolean' ? this.$mantainAspectRatio() as boolean : false,
        plugins: {
          legend: {
            display: this.$displayTopLabels() && dataset.labels.length > 0,
          },
          datalabels: {
            display: false,
          },
        },
        scales: {
          x: {
            grid: {
              display: !(dataset.hideXGrid ?? false),
            },
            border: {
              display: !(dataset.hideXGrid ?? false),
            },
          },
          y: {
            beginAtZero: dataset.beginAtZero,
            grid: {
              display: !(dataset.hideYGrid ?? false),
            },
            border: {
              display: !(dataset.hideYGrid ?? false),
            },
          },
        },
      },
    });
  }

  // Devuelve el tamaño adecuado de las barras para una gráfica agrupada en función de la cantidad de grupos y barras por grupo
  private getGroupedBarsChartBarsPercentage = (groupsAmount: number, barsAmount: number): { barPercentage: number, categoryPercentage: number } => {
    let barPercentage: number = this.getBarChartBarsPercentage(barsAmount);
    let categoryPercentage: number = 1;

    switch (groupsAmount) {
      case 2:
        barPercentage += 0.1;
        categoryPercentage = 0.9;
        break;

      case 3:
        barPercentage += 0.1;
        categoryPercentage = 0.85;
        break;

      case 4:
        barPercentage += 0.1;
        categoryPercentage = 0.9;
        break;

      case 5:
        barPercentage += 0.1;
        categoryPercentage = 0.85;
        break;

      case 6:
        barPercentage += 0.1;
        categoryPercentage = 0.82;
        break;

      case 7:
        barPercentage += 0.1;
        categoryPercentage = 0.85;
        break;

      case 8:
        barPercentage = 0.9;
        categoryPercentage = 0.85;
        break;

      case 9:
        barPercentage = 0.95;
        categoryPercentage = 0.85;
        break;

      case 10:
        barPercentage = 0.96;
        categoryPercentage = 0.86;
        break;

      case 11:
        barPercentage = 0.97;
        categoryPercentage = 0.87;
        break;

      default:
        barPercentage = 0.98;
        categoryPercentage = 0.9;
    }

    return { barPercentage, categoryPercentage };
  };

  // Crea una gráfica de barras agrupadas
  private createGroupedBarsChart(dataset: GroupedBarsChartDataset) {
    if (!dataset.data.length)
      return;

    if (!dataset.labels.length)
      throw Error(`Gráfica de barras agrupadas - la cantidad de etiquetas para las barras no puede ser 0.`);

    const barsPerGroup = dataset.data[0].length;

    dataset.data.forEach((d, index) => {
      if (d.length !== barsPerGroup)
        throw Error(`Gráfica de barras agrupadas - la cantidad de datos en el grupo con índice ${ index } (${ d.length }) no coincide con la cantidad de datos en el grupo con índice 0 (${ barsPerGroup }).`);
    });

    const groupsCount = dataset.data.length;

    if (dataset.groupLabels.length !== groupsCount)
      throw Error(`Gráfica de barras agrupadas - la cantidad de etiquetas para los grupos (${ dataset.groupLabels.length }) no coincide con la cantidad de los grupos (${ groupsCount }).`);

    if (dataset.labels.length !== barsPerGroup)
      throw Error(`Gráfica de barras agrupadas - la cantidad de etiquetas para las barras (${ dataset.labels.length }) no coincide con la cantidad de barras por grupo (${ barsPerGroup }).`);

    if (dataset.colors.length !== barsPerGroup)
      throw Error(`Gráfica de barras agrupadas - la cantidad de colores para las barras (${ dataset.colors.length }) no coincide con la cantidad de barras por grupo (${ barsPerGroup }).`);

    const { barPercentage, categoryPercentage } = this.getGroupedBarsChartBarsPercentage(groupsCount, barsPerGroup);

    if (this.chart)
      this.chart.destroy();
    this.chart = new ChartJS(this.canvas, {
      type: 'bar',
      data: {
        labels: dataset.groupLabels,
        datasets: Array.from({ length: barsPerGroup }, (_, index) => ({
          data: dataset.data.map(d => d[index]),
          label: dataset.labels[index],
          backgroundColor: dataset.colors[index],
          borderRadius: dataset.borderRadius ?? 0,
          barPercentage,
          categoryPercentage,
        })),
      },
      options: {
        responsive: typeof this.$responsive() === 'boolean' ? this.$responsive() as boolean : true,
        maintainAspectRatio: typeof this.$mantainAspectRatio() === 'boolean' ? this.$mantainAspectRatio() as boolean : false,
        plugins: {
          legend: {
            display: this.$displayTopLabels(),
            labels: {
              usePointStyle: true,
              pointStyle: 'rectRounded',
              boxWidth: 10,
              boxHeight: 10,
              font: { weight: 'bold' as const },
            },
          },
          datalabels: {
            display: false,
          },
          tooltip: dataset.valueFormat === 'percentage' ? {
            callbacks: {
              label: context => `${ context.dataset.label }: ${ new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(context.parsed.y ?? 0) }%`,
            },
          } : undefined,
        },
        scales: {
          x: {
            grid: {
              display: !(dataset.hideXGrid ?? false),
            },
            border: {
              display: !(dataset.hideXGrid ?? false),
            },
          },
          y: {
            beginAtZero: dataset.beginAtZero,
            suggestedMax: dataset.valueFormat === 'percentage' ? 100 : undefined,
            ticks: dataset.valueFormat === 'percentage' ? {
              callback: value => `${ value }%`,
            } : undefined,
            grid: {
              display: !(dataset.hideYGrid ?? false),
            },
            border: {
              display: !(dataset.hideYGrid ?? false),
            },
          },
        },
      },
    });
  }

  // Crea una gráfica de dona
  private createDoughnutChart(dataset: DoughnutChartDataset) {
    if (!dataset.data.length) return;

    if (dataset.labels.length > 0 && dataset.labels.length !== dataset.data.length)
      throw Error(`Gráfica de dona - la cantidad de etiquetas (${ dataset.labels.length }) es distinta a la cantidad de datos (${ dataset.data.length }).`);
    if (dataset.colors.length !== dataset.data.length)
      throw Error(`Gráfica de dona - la cantidad de colores (${ dataset.colors.length }) es distinta a la cantidad de datos (${ dataset.data.length }).`);

    if (this.chart)
      this.chart.destroy();
    this.chart = new ChartJS(this.canvas, {
      type: 'doughnut',
      data: {
        labels: dataset.labels,
        datasets: [
          {
            data: dataset.data,
            backgroundColor: dataset.colors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: typeof this.$responsive() === 'boolean' ? this.$responsive() as boolean : true,
        maintainAspectRatio: typeof this.$mantainAspectRatio() === 'boolean' ? this.$mantainAspectRatio() as boolean : true,
        cutout: '69%',
        plugins: {
          legend: {
            display: this.$displayTopLabels(),
          },
          datalabels: {
            display: dataset.show !== 'none',
            color: 'white',
            textStrokeColor: 'black',
            textStrokeWidth: 3,
            font: {
              weight: 'bold',
              size: 14,
            },
            formatter: (value, context) => { // Formateo de etiquetas
              const strValue = commas(value).toString();
              const minValueDisplay: number = dataset.minValueDisplay ?? 0;

              if (dataset.show === 'values')
                return Number(value) < minValueDisplay ? '' : strValue;

              const total: number = context.chart.data.datasets[0].data.reduce<number>((acc: number, current: unknown) => acc + (current as number), 0);
              const percentage: number = Number((value * 100 / total).toFixed(2));
              return percentage < minValueDisplay ? '' : `${ percentage }%`.replace('.00%', '%');
            },
          },
          tooltip: {
            callbacks: {
              label: item => { // Formateo de tooltips
                const value = item.parsed;
                const minValueDisplay: number = dataset.minValueDisplay ?? 0;
                const total: number = item.dataset.data.reduce<number>((total, d) => total + d, 0);
                const percentage: number = Number((value * 100 / total).toFixed(2));
                const strValue = commas(value).toString();
                const strPercentage = `${ percentage }%`.replace('.00%', '%');
                const strValuePercentage = `${ strValue } - ${ strPercentage }`;

                if (dataset.show === 'none')
                  return strValuePercentage;
                if (dataset.show === 'percentages')
                  return percentage >= minValueDisplay ? strValue : strValuePercentage;
                return value >= minValueDisplay ? strPercentage : strValuePercentage;
              },
            },
          },
        },
      },
    });
  }

  // Crea una gráfica de barras horizontales
  private createHorizontalBarChart(dataset: HorizontalBarChartDataset) {
    if (!dataset.data.length) return;

    if (dataset.labels.length > 0 && dataset.labels.length !== dataset.data.length)
      throw Error(`Gráfica de barras horizontales - la cantidad de etiquetas (${ dataset.labels.length }) es distinta a la cantidad de datos (${ dataset.data.length }).`);
    if (dataset.colors.length !== dataset.data.length)
      throw Error(`Gráfica de barras horizontales - la cantidad de colores (${ dataset.colors.length }) es distinta a la cantidad de datos (${ dataset.data.length }).`);

    const barPercentage = this.getBarChartBarsPercentage(dataset.data.length);
    const borderRadius = dataset.borderRadius ?? 0;
    const maxValue = Math.max(...dataset.data);
    // Log requiere un rango positivo; con todo en 0 (o negativo, no debería pasar aquí) se cae a lineal.
    const useLogScale = this.$useLogarithmicScale() && maxValue > 0;

    const hasTrack = !!dataset.trackColor;
    const trackColorValue = dataset.trackColor;
    const trackRadius = borderRadius;

    const resolveTrackColor = (): string => {
      if (trackColorValue === true)
        return getComputedStyle(document.documentElement).getPropertyValue('--bar-chart-track').trim();
      return trackColorValue ?? '';
    };

    const trackPlugin = hasTrack ? {
      id: 'horizontalBarTrack',
      beforeDatasetsDraw(chart: any) {
        const { ctx } = chart;
        const meta = chart.getDatasetMeta(0);
        if (!meta?.data?.length) return;

        const color = resolveTrackColor();
        const xScale = chart.scales['x'];
        const maxPixel = xScale.getPixelForValue(maxValue);
        const minPixel = xScale.getPixelForValue(0);
        const trackWidth = maxPixel - minPixel;

        meta.data.forEach((bar: any) => {
          const y = bar.y;
          const height = bar.height;
          const x = minPixel;

          ctx.save();
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.roundRect(x, y - height / 2, trackWidth, height, trackRadius);
          ctx.fill();
          ctx.restore();
        });
      },
    } : null;

    if (this.chart)
      this.chart.destroy();
    this.chart = new ChartJS(this.canvas, {
      type: 'bar',
      data: {
        labels: dataset.labels,
        datasets: [{
          data: dataset.data,
          backgroundColor: dataset.colors,
          borderWidth: 0,
          borderRadius,
          borderSkipped: false,
          barPercentage,
        }],
      },
      options: {
        indexAxis: 'y',
        responsive: typeof this.$responsive() === 'boolean' ? this.$responsive() as boolean : true,
        maintainAspectRatio: typeof this.$mantainAspectRatio() === 'boolean' ? this.$mantainAspectRatio() as boolean : false,
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            display: false,
          },
        },
        scales: {
          x: {
            type: useLogScale ? 'logarithmic' : 'linear',
            // Chart.js autocalcula el mínimo log pegado al valor más chico si no se fija;
            // con pocas categorías eso deja la barra menor casi invisible (rango minúsculo
            // entre min y su propio valor). Piso fijo por debajo de 1 (el valor mínimo real
            // posible) para que la escala completa sea consistente sin importar cuántas
            // categorías haya, y para que una categoría con valor=1 no dé una barra de
            // longitud cero (log10(1) - log10(min) tiene que ser > 0).
            min: useLogScale ? 0.5 : undefined,
            beginAtZero: useLogScale ? undefined : dataset.beginAtZero,
            display: !(dataset.hideXGrid ?? false),
            max: maxValue,
            grid: {
              display: !(dataset.hideXGrid ?? false),
            },
            ticks: {
              display: false,
            },
            border: {
              display: false,
            },
          },
          y: {
            display: true,
            ticks: {
              display: this.$displayTopLabels(),
            },
            border: {
              display: !(dataset.hideYGrid ?? false),
            },
            grid: {
              display: !(dataset.hideYGrid ?? false),
            },
          },
        },
      },
      plugins: trackPlugin ? [trackPlugin] : [],
    });
  }

  // Crea una gráfica de barras apiladas con línea punteada
  private createStackedBarLineChart(dataset: StackedBarLineChartDataset) {
    const seriesCount = dataset.barData.length;
    const groupsCount = dataset.barData[0]?.length ?? 0;

    if (!groupsCount) return;

    if (dataset.barLabels.length !== seriesCount)
      throw Error(`Gráfica apilada - la cantidad de etiquetas (${ dataset.barLabels.length }) no coincide con la cantidad de series (${ seriesCount }).`);

    if (dataset.barColors.length !== seriesCount)
      throw Error(`Gráfica apilada - la cantidad de colores (${ dataset.barColors.length }) no coincide con la cantidad de series (${ seriesCount }).`);

    if (dataset.groupLabels.length !== groupsCount)
      throw Error(`Gráfica apilada - la cantidad de etiquetas de grupo (${ dataset.groupLabels.length }) no coincide con la cantidad de grupos (${ groupsCount }).`);

    // La línea es opcional: sin datos (p. ej. un total no aditivo que llega null) se dibujan solo las barras.
    const hasLine = dataset.lineData.length > 0;
    if (hasLine && dataset.lineData.length !== groupsCount)
      throw Error(`Gráfica apilada - la cantidad de datos de línea (${ dataset.lineData.length }) no coincide con la cantidad de grupos (${ groupsCount }).`);

    const barDatasets = dataset.barData.map((series, index) => ({
      type: 'bar' as const,
      data: series,
      label: dataset.barLabels[index],
      backgroundColor: dataset.barColors[index],
      borderRadius: dataset.borderRadius ?? 0,
      stack: 'stack0',
    }));

    const lineDataset = {
      type: 'line' as const,
      data: dataset.lineData,
      label: dataset.lineLabel,
      borderColor: dataset.lineColor,
      backgroundColor: 'transparent',
      borderWidth: 2.5,
      borderDash: [6, 4],
      pointRadius: 0,
      tension: 0,
      fill: false,
    };

    if (this.chart)
      this.chart.destroy();
    this.chart = new ChartJS(this.canvas, {
      type: 'bar',
      data: {
        labels: dataset.groupLabels,
        datasets: hasLine ? [...barDatasets, lineDataset] : barDatasets,
      },
      options: {
        responsive: typeof this.$responsive() === 'boolean' ? this.$responsive() as boolean : true,
        maintainAspectRatio: typeof this.$mantainAspectRatio() === 'boolean' ? this.$mantainAspectRatio() as boolean : false,
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            display: false,
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: {
              display: !(dataset.hideXGrid ?? false),
            },
            border: {
              display: !(dataset.hideXGrid ?? false),
            },
          },
          y: {
            stacked: true,
            beginAtZero: dataset.beginAtZero,
            grid: {
              display: !(dataset.hideYGrid ?? false),
            },
            border: {
              display: !(dataset.hideYGrid ?? false),
            },
          },
        },
      },
    });
  }

  // Crea una gráfica de líeneas
  private createLineChart(dataset: LineChartDataset) {
    if (!dataset.data.length)
      return;

    const linesCount = dataset.data.length;

    if (dataset.linesLabels.length > 0 && dataset.linesLabels.length !== linesCount)
      throw Error(`Gráfica de líneas - la cantidad de etiquetas para las líneas (${ dataset.linesLabels.length }) no coincide con la cantidad de líneas (${ linesCount }).`);

    if (dataset.colors.length !== linesCount)
      throw Error(`Gráfica de líneas - la cantidad de colores para las líneas (${ dataset.colors.length }) no coincide con la cantidad de líneas (${ linesCount }).`);

    const pointsPerLine = dataset.data[0].length;

    if (!dataset.labels.length)
      throw Error(`Gráfica de líneas - la cantidad de etiquetas para los puntos no puede ser 0.`);

    if (dataset.labels.length !== pointsPerLine)
      throw Error(`Gráfica de líneas - la cantidad de etiquetas para los puntos (${ dataset.labels.length }) no coincide con la cantidad de puntos por línea (${ pointsPerLine }).`);

    dataset.data.forEach((d, index) => {
      if (d.length !== pointsPerLine)
        throw Error(`Gráfica de líneas - la cantidad de datos de la línea con índice ${ index } (${ d.length }) no coincide con la cantidad de datos de la línea con índice 0 (${ pointsPerLine }).`);
    });

    if (this.chart)
      this.chart.destroy();
    this.chart = new ChartJS(this.canvas, {
      type: 'line',
      data: {
        labels: dataset.labels,
        datasets: dataset.data.map((_, index) => ({
          data: dataset.data[index],
          label: dataset.linesLabels.length > 0 ? dataset.linesLabels[index] : undefined,
          backgroundColor: dataset.colors[index],
          fill: dataset.fill,
          borderWidth: 3,
          borderColor: dataset.colors[index],
          tension: dataset.tension ?? 0,
        })).sort((a, b) => a.data.reduce((acc, v) => acc + v, 0) - b.data.reduce((acc, v) => acc + v, 0)),
      },
      options: {
        responsive: typeof this.$responsive() === 'boolean' ? this.$responsive() as boolean : true,
        maintainAspectRatio: typeof this.$mantainAspectRatio() === 'boolean' ? this.$mantainAspectRatio() as boolean : false,
        plugins: {
          legend: {
            display: this.$displayTopLabels() && dataset.linesLabels.length > 0,
          },
          datalabels: {
            display: false,
          },
        },
        scales: {
          y: {
            beginAtZero: dataset.beginAtZero,
          },
        },
        elements: {
          point: {
            radius: dataset.pointRadius ?? 6.25,
          },
        },
      },
    });
  }

  // Crea una gráfica de treemap
  private createTreemapChart(dataset: TreemapChartDataset) {
    if (!dataset.data.length) return;
    if (dataset.labels.length !== dataset.data.length)
      throw Error(`Gráfica treemap - la cantidad de etiquetas (${ dataset.labels.length }) es distinta a la cantidad de datos (${ dataset.data.length }).`);
    if (dataset.colors.length !== dataset.data.length)
      throw Error(`Gráfica treemap - la cantidad de colores (${ dataset.colors.length }) es distinta a la cantidad de datos (${ dataset.data.length }).`);

    const treeData = dataset.labels.map((label, i) => ({
      label,
      value: dataset.data[i],
      color: dataset.colors[i],
    }));

    if (this.chart)
      this.chart.destroy();
    this.chart = new ChartJS(this.canvas, {
      type: 'treemap' as any,
      data: {
        datasets: [{
          tree: treeData,
          key: 'value',
          groups: ['label'],
          labels: {
            display: true,
            overflow: 'fit' as any,
            align: 'left' as any,
            position: 'top' as any,
            font: { size: 14, weight: 'bold' as any },
            color: 'white',
            textStrokeColor: 'rgba(0,0,0,0.75)',
            textStrokeWidth: 4,
            formatter: (ctx: any) => ctx.raw?.g ? ctx.raw.g : '',
          },
          backgroundColor: (ctx: any) => {
            if (ctx.type !== 'data') return 'transparent';
            return treeData.find(d => d.label === ctx.raw?.g)?.color ?? '#ccc';
          },
          spacing: 2,
          borderWidth: 0,
        } as any],
      },
      options: {
        responsive: typeof this.$responsive() === 'boolean' ? this.$responsive() as boolean : true,
        maintainAspectRatio: typeof this.$mantainAspectRatio() === 'boolean' ? this.$mantainAspectRatio() as boolean : false,
        plugins: {
          legend: { display: false },
          datalabels: { display: false },
          tooltip: {
            callbacks: {
              title: (items: any[]) => items[0]?.raw?.g ?? '',
              label: (item: any) => ` ${ commas(item.raw?.v ?? 0) }`,
            },
          },
        },
      } as any,
    });
  }
}
